'use client';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { User, Session } from '@supabase/supabase-js';
import { realtimeService } from '@/services/realtime.service';
import { useRouter } from 'next/navigation';

interface UserProfile {
  id: string
  email: string
  full_name?: string
  avatar_url?: string
  stripe_customer_id?: string
  is_admin?: boolean
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  profileLoading: boolean
  signIn: (email: string, password: string) => Promise<any>
  signUp: (email: string, password: string, fullName?: string) => Promise<any>
  signOut: () => Promise<any>
  signInWithOAuth: (provider: 'google') => Promise<any>
  updateProfile: (updates: Partial<UserProfile>) => Promise<any>
  resetPassword: (email: string) => Promise<any>
  updatePassword: (newPassword: string) => Promise<any>
  isAuthenticated: boolean
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const profileOperations = {
    async load(userId: string) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single()
        if (!error && data) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    },
  }

  const authStateHandlers = {
    onChange: (_event: string | null, session: Session | null) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        profileOperations.load(session.user.id)
      } else {
        profileOperations.clear()
      }
    },
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      authStateHandlers.onChange(null, session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(authStateHandlers.onChange)

    // Cleanup all real-time subscriptions on logout
    return () => {
      realtimeService.unsubscribeAll();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      })
      return { data, error }
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Network error. Please try again.' } 
      }
    }
  }

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName || email.split('@')[0],
          },
        },
      })
      return { data, error }
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Network error. Please try again.' } 
      }
    }
  }

  const signOut = async () => {
    try {
      // Cleanup all real-time subscriptions before signing out
      realtimeService.unsubscribeAll();
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      router.push('/login');
      return { error: null };
    } catch (error: any) {
      return { error };
    }
  }

  const signInWithOAuth = async (provider: 'google') => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })
      return { data, error }
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'OAuth sign-in failed. Please try again.' } 
      }
    }
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()
      if (!error && data) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Network error. Please try again.' } 
      }
    }
  }

  const resetPassword = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error: any) {
      return { 
        data: null, 
        error: { message: error.message || 'Failed to send password reset email' } 
      };
    }
  }

  const updatePassword = async (newPassword: string) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error: any) {
      return { 
        data: null, 
        error: { message: error.message || 'Failed to update password' } 
      };
    }
  }

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    signInWithOAuth,
    updateProfile,
    resetPassword,
    updatePassword,
    isAuthenticated: !!user,
    isAdmin: userProfile?.is_admin === true,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}